import { useEffect, useState } from 'react';
import { getS3PresignedUrl } from '@/utils/s3';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Eye as EyeIcon,
  File as FileIcon,
  Image as ImageIcon,
  Paperclip as PaperclipIcon,
  FilePdf as PdfIcon,
  Plus as PlusIcon,
  X as XIcon,
} from '@phosphor-icons/react';

import type { File } from '@/types/v2/file';
import type { Request } from '@/types/v2/request';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { hasCommunityRolesV2 } from '@/utils/has-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { SectionLabel } from './request-details-card';
import AddAttachmentsDialog from './add-attachment-dialog';

interface RequestAttachmentsProps {
  request: Request;
  isLoading: boolean;
  error: Error | null;
}

function getFileIcon(fileType: string) {
  if (fileType.includes('image')) return <ImageIcon size={16} />;
  if (fileType.includes('pdf')) return <PdfIcon size={16} />;
  return <FileIcon size={16} />;
}

function getTypeLabel(fileType: string): string {
  if (fileType.includes('image')) return 'Image';
  if (fileType.includes('pdf')) return 'PDF';
  if (fileType.includes('csv')) return 'CSV';
  if (fileType.includes('word') || fileType.includes('document')) return 'Doc';
  if (fileType.includes('sheet') || fileType.includes('excel')) return 'Excel';
  return 'File';
}

export function RequestAttachments({ request, isLoading, error }: RequestAttachmentsProps): React.JSX.Element {
  const { user, community } = useUser();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [fileLoadError, setFileLoadError] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const dialog = useDialog();

  const isCreator = user?.id && request?.created_by?.id && String(user.id) === String(request.created_by.id);
  const isManagerOrBoard = hasCommunityRolesV2(community, [COMMUNITY_ROLES.COMMUNITY_MANAGER, COMMUNITY_ROLES.BOARD_MEMBER]);
  const canAddAttachment = isCreator || isManagerOrBoard;

  useEffect(() => {
    if (selectedFile) {
      setIsFileLoading(true);
      getS3PresignedUrl(selectedFile)
        .then((url) => { setFileUrl(url); setFileLoadError(false); })
        .catch(() => { setFileLoadError(true); })
        .finally(() => { setIsFileLoading(false); });
    }
  }, [selectedFile]);

  const handleClose = () => {
    setSelectedFile(null);
    setFileUrl('');
    setFileLoadError(false);
  };

  const renderPreview = () => {
    if (fileLoadError && selectedFile) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error" variant="body1">Could not load file</Typography>
          <Typography color="text.secondary" variant="body2">{selectedFile.name}</Typography>
        </Box>
      );
    }
    if (isFileLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (!selectedFile) return null;
    if (selectedFile.type.includes('pdf')) {
      return <iframe src={fileUrl} style={{ width: '100%', height: '80vh', border: 'none' }} title={selectedFile.name} />;
    }
    if (selectedFile.type.includes('image')) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'common.black',
            maxHeight: '80vh',
            p: 1,
          }}
        >
          <Box
            component="img"
            alt={selectedFile.name}
            loading="lazy"
            onError={() => setFileLoadError(true)}
            src={fileUrl}
            sx={{
              maxWidth: '100%',
              maxHeight: '80vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      );
    }
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary" variant="body2">This file type cannot be previewed.</Typography>
        <Button color="primary" download={selectedFile.name} href={fileUrl} sx={{ mt: 2 }} variant="outlined" size="small">
          Download
        </Button>
      </Box>
    );
  };

  const files = request?.files ?? [];

  return (
    <>
      <Box>
        <SectionLabel
          icon={<PaperclipIcon size={14} />}
          action={
            canAddAttachment ? (
              <Tooltip title="Add attachment">
                <IconButton onClick={dialog.handleOpen} size="small" sx={{ p: 0.25 }}>
                  <PlusIcon size={14} />
                </IconButton>
              </Tooltip>
            ) : undefined
          }
        >
          Attachments
        </SectionLabel>

        <Box sx={{ mt: 1.5 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : files.length === 0 ? (
            <Typography color="text.disabled" sx={{ fontStyle: 'italic' }} variant="body2">
              No attachments
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {files.map((file, i) => (
                <Stack
                  key={i}
                  direction="row"
                  onClick={() => setSelectedFile(file as unknown as File)}
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'background-color 0.12s',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ color: 'text.secondary', display: 'flex', flexShrink: 0 }}>
                    {getFileIcon(file.type)}
                  </Box>
                  <Typography noWrap sx={{ flex: 1, fontSize: '0.8125rem', fontWeight: 500, minWidth: 0 }}>
                    {file.name}
                  </Typography>
                  <Typography color="text.disabled" sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
                    {getTypeLabel(file.type)}
                  </Typography>
                  <EyeIcon size={13} style={{ color: 'var(--mui-palette-text-disabled)', flexShrink: 0 }} />
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      {/* Preview dialog */}
      <Dialog
        fullWidth
        maxWidth="lg"
        onClose={handleClose}
        open={Boolean(selectedFile)}
        PaperProps={{ sx: { maxHeight: '92vh' } }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography noWrap variant="body2" sx={{ fontWeight: 600, pr: 1 }}>{selectedFile?.name}</Typography>
          <IconButton onClick={handleClose} size="small" sx={{ flexShrink: 0 }}>
            <XIcon size={18} />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>{renderPreview()}</DialogContent>
      </Dialog>

      {canAddAttachment && <AddAttachmentsDialog onClose={dialog.handleClose} open={dialog.open} />}
    </>
  );
}
